/**
 * Salary Report (BE-14 report 18)
 *
 * Approved salaries by person and month (refunds netted, standing salary and the difference, owners flagged), the
 * totals by month, and the months not paid yet with a link to enter the salary.
 * Route: /reports/salary
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Grid, TextField, MenuItem, Typography, Link, Chip, Box } from '@mui/material';
import ReportPage, { ReportTable, formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { expenseService } from '../../services/accounting/expense.service';
import { serverMessage } from '../../utils/apiConvert';

const TYPE_LABEL = { PAID: 'Paid', PAYABLE: 'Payable' };
const EMPLOYMENT_LABEL = { EMPLOYEE: 'Employee', EXTERNAL: 'External' };
const money = (v) => (v === null || v === undefined ? '-' : formatCurrency(v));
const pad = (n) => String(n).padStart(2, '0');
const monthLabel = (d) => {
  const [y, m] = String(d).split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};
const maskIban = (iban) => (iban ? `•••• ${String(iban).slice(-4)}` : null);
const paidTo = (r) => (r.paidToBank || r.paidToIban ? [r.paidToBank, maskIban(r.paidToIban)].filter(Boolean).join(' ') : '-');

const SalaryReport = ({ currentUser }) => {
  const now = new Date();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState(`${now.getFullYear()}-01`);
  const [to, setTo] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [userId, setUserId] = useState('');
  const [people, setPeople] = useState([]);

  useEffect(() => {
    expenseService.getTeamMembers(currentUser?.typingCenterId).then(setPeople).catch(() => setPeople([]));
  }, [currentUser?.typingCenterId]);

  const fetchData = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getSalaryReport({ from: `${from}-01`, to: `${to}-01`, userId }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the salary report'));
    } finally {
      setLoading(false);
    }
  }, [from, to, userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const missing = report?.missing || [];
  const monthColumns = [
    { key: 'salaryMonth', label: 'Month', render: (r) => monthLabel(r.salaryMonth) },
    { key: 'standingSalary', label: 'Standing', align: 'right', render: (r) => money(r.standingSalary) },
    { key: 'amount', label: 'Salary', align: 'right', render: (r) => formatCurrency(r.amount) },
    { key: 'refunded', label: 'Refunded', align: 'right', render: (r) => (Number(r.refunded) ? formatCurrency(r.refunded) : '-') },
    { key: 'net', label: 'Net', align: 'right', render: (r) => formatCurrency(r.net), total: (x) => formatCurrency(x.total) },
    { key: 'difference', label: 'Difference', align: 'right', render: (r) => money(r.difference) },
    { key: 'expenseType', label: 'Type', render: (r) => TYPE_LABEL[r.expenseType] || r.expenseType },
    { key: 'paidAt', label: 'Paid on', render: (r) => (r.paidAt ? formatDate(r.paidAt) : (r.expenseType === 'PAYABLE' ? 'Not paid' : '-')) },
    { key: 'paidTo', label: 'Paid to', render: paidTo },
    {
      key: 'expenseNumber', label: 'Expense',
      render: (r) => <Link component={RouterLink} to={`/accounting/expenses/${r.expenseId}`}>{r.expenseNumber}</Link>,
    },
  ];

  const byMonthColumns = [
    { key: 'salaryMonth', label: 'Month', render: (r) => monthLabel(r.salaryMonth) },
    { key: 'people', label: 'People', align: 'right' },
    { key: 'total', label: 'Total', align: 'right', render: (r) => formatCurrency(r.total), total: (x) => formatCurrency(x.total) },
  ];

  const missingColumns = [
    { key: 'name', label: 'Person' },
    { key: 'salaryMonth', label: 'Month', render: (r) => monthLabel(r.salaryMonth) },
    { key: 'standingSalary', label: 'Standing salary', align: 'right', render: (r) => formatCurrency(r.standingSalary) },
    {
      key: 'pending', label: 'Status',
      render: (r) => (r.pending ? <Chip size="small" color="warning" label="Pending approval" /> : 'Not entered'),
    },
    {
      key: 'action', label: '',
      render: (r) => (
        <Link component={RouterLink} to={`/accounting/expenses/new?payeeId=${r.userId}&salaryMonth=${String(r.salaryMonth).slice(0, 7)}`}>
          Enter salary
        </Link>
      ),
    },
  ];

  const cards = report ? [
    { label: 'Salaries paid', value: formatCurrency(report.total) },
    { label: 'People', value: (report.people || []).length },
    { label: 'Not paid yet', value: `${missing.length} months`, tone: missing.length > 0 ? 'warn' : undefined },
    { label: 'Unpaid payables', value: formatCurrency(report.unpaidPayables), tone: Number(report.unpaidPayables) > 0 ? 'bad' : undefined },
  ] : [];

  return (
    <ReportPage
      title="Salary Report"
      subtitle={`Approved salaries ${monthLabel(`${from}-01`)} – ${monthLabel(`${to}-01`)}, by person and month`}
      exportType="salary" exportParams={{ from: `${from}-01`, to: `${to}-01`, userId }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={byMonthColumns} rows={report?.byMonth || []} rowKey={(r) => r.salaryMonth}
      totals={report} totalsLabel="TOTAL"
      emptyMessage="No salary was approved in this range."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="From month" type="month" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="To month" type="month" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth size="small" label="Person" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <MenuItem value="">Everyone</MenuItem>
              {people.map((p) => <MenuItem key={p.id} value={p.id}>{p.fullName || p.username}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      )}
      footer={(
        <ReportTable
          title="Not paid yet"
          columns={missingColumns} rows={missing} rowKey={(r) => `${r.userId}-${r.salaryMonth}`}
          emptyMessage="Every salary is entered for this range."
        />
      )}
    >
      {!loading && (report?.people || []).map((p) => (
        <ReportTable
          key={p.userId}
          title={`${p.name}${p.owner ? ' · Owner' : ''}${EMPLOYMENT_LABEL[p.employmentType] ? ` · ${EMPLOYMENT_LABEL[p.employmentType]}` : ''}`}
          columns={monthColumns} rows={p.months} rowKey={(r) => `${r.expenseId}`}
          totals={p} totalsLabel="TOTAL" tableOnPhone numbered={false}
        />
      ))}
      {!loading && (report?.people || []).length > 0 && (
        <Box sx={{ mb: 1 }}><Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>By month</Typography></Box>
      )}
    </ReportPage>
  );
};

export default SalaryReport;
